import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Localization } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { PLAAttributeDefinition, PLA_CUBE_TOTAL_KEY, getPLACubeCellValue, isPLANumberValueType, PLAElementRecord, PLAProject, setPLACubeCellValue } from "./PLAState";
import { formatPLAAttributeValue } from "./PLAFormatting";
import "./PLATable.css";

const overlayRoot = () => document.getElementById("Mit-NonBlockingOverlay") || document.body;
export const PLA_CUBE_ANY_FILTER = "__PLA_CUBE_ANY__";
export type PLACubeFilter = Record<number, string>;

function normalizeCubeInstanceSelection(keys: string[] | undefined, dimensionOptions: ReturnType<typeof getPLACubeDimensionOptions>): string[] {
   return dimensionOptions.map((option, index) => {
      const existingKey = keys?.[index];
      return existingKey && option.elements.some((element) => element.id === existingKey) ? existingKey : option.elements[0]?.id || PLA_CUBE_TOTAL_KEY;
   });
}

function cubeInputValue(value: any): string {
   return value === undefined || value === null ? "" : String(value);
}

function parseCubeInputValue(value: string | boolean, definition: PLAAttributeDefinition): any {
   if (definition.valueType === "boolean") return value === true || value === "true";
   if (isPLANumberValueType(definition.valueType)) return value === "" ? "" : Number(value);
   return value;
}

export function getPLACubeDimensionOptions(project: PLAProject, elements: PLAElementRecord[], definition: PLAAttributeDefinition) {
   return (definition.cubeDimensions || []).map((dimension) => ({
      dimension,
      name: project.elementTypes[dimension.elementTypeId]?.name || dimension.elementTypeId,
      elements: elements.filter((element) => element.elementType === dimension.elementTypeId).sort((a, b) => a.name.localeCompare(b.name))
   }));
}

export function getPLACubeElementName(element: PLAElementRecord): string {
   return element.name || element.id;
}

function getCubeFilterCombinations(dimensionOptions: ReturnType<typeof getPLACubeDimensionOptions>, filter: PLACubeFilter | undefined): string[][] {
   return dimensionOptions.reduce<string[][]>((combinations, option, dimensionIndex) => {
      const selectedKey = filter?.[dimensionIndex];
      const keys = selectedKey && selectedKey !== PLA_CUBE_ANY_FILTER ? [selectedKey] : option.elements.map((element) => element.id);
      return combinations.flatMap((combination) => (keys.length ? keys : [PLA_CUBE_TOTAL_KEY]).map((key) => [...combination, key]));
   }, [[]]);
}

export function getPLACubeFilteredValue(value: any, definition: PLAAttributeDefinition, project: PLAProject, elements: PLAElementRecord[], filter?: PLACubeFilter): any {
   const dimensionOptions = getPLACubeDimensionOptions(project, elements, definition);
   if (!isPLANumberValueType(definition.valueType)) {
      const keys = dimensionOptions.map((_option, dimensionIndex) => {
         const selectedKey = filter?.[dimensionIndex];
         return selectedKey && selectedKey !== PLA_CUBE_ANY_FILTER ? selectedKey : PLA_CUBE_TOTAL_KEY;
      });
      return getPLACubeCellValue(value, keys);
   }

   let hasValue = false;
   const total = getCubeFilterCombinations(dimensionOptions, filter).reduce((sum, keys) => {
      const numericValue = Number(getPLACubeCellValue(value, keys));
      if (!Number.isFinite(numericValue)) return sum;
      hasValue = true;
      return sum + numericValue;
   }, 0);
   if (hasValue) return total;

   const totalKeys = dimensionOptions.map((_option, dimensionIndex) => {
      const selectedKey = filter?.[dimensionIndex];
      return selectedKey && selectedKey !== PLA_CUBE_ANY_FILTER ? selectedKey : PLA_CUBE_TOTAL_KEY;
   });
   return getPLACubeCellValue(value, totalKeys);
}

function CubeGridModal(props: {
   project: PLAProject;
   elements: PLAElementRecord[];
   definition: PLAAttributeDefinition;
   value: any;
   readOnly?: boolean;
   onChange?: (value: any) => void;
   onClose: () => void;
}) {
   const dimensions = props.definition.cubeDimensions || [];
   const dimensionOptions = useMemo(() => getPLACubeDimensionOptions(props.project, props.elements, props.definition), [props.project, props.elements, props.definition]);
   const [selectedKeys, setSelectedKeys] = useState<string[]>(() => normalizeCubeInstanceSelection(undefined, dimensionOptions));

   useEffect(() => {
      setSelectedKeys((previous) => normalizeCubeInstanceSelection(previous, dimensionOptions));
   }, [dimensionOptions]);

   const expandedDimensionIndexes = dimensions.reduce<number[]>((indexes, dimension, index) => dimension.expanded && indexes.length < 2 ? [...indexes, index] : indexes, []);
   const selectorDimensionIndexes = dimensions.map((_dimension, index) => index).filter((index) => !expandedDimensionIndexes.includes(index));
   const rowDimension = expandedDimensionIndexes[0] !== undefined ? dimensionOptions[expandedDimensionIndexes[0]] : undefined;
   const columnDimension = expandedDimensionIndexes[1] !== undefined ? dimensionOptions[expandedDimensionIndexes[1]] : undefined;
   const selectedCellKeys = normalizeCubeInstanceSelection(selectedKeys, dimensionOptions);
   const inputType = isPLANumberValueType(props.definition.valueType) ? "number" : props.definition.valueType === "date" ? "date" : "text";
   const showNumericTotals = isPLANumberValueType(props.definition.valueType);
   const rowKeys = rowDimension ? rowDimension.elements.map((element) => element.id) : [];
   const columnKeys = columnDimension ? columnDimension.elements.map((element) => element.id) : [PLA_CUBE_TOTAL_KEY];

   const updateSelectedKey = (dimensionIndex: number, key: string) => {
      setSelectedKeys((previous) => normalizeCubeInstanceSelection(previous, dimensionOptions).map((existingKey, index) => index === dimensionIndex ? key : existingKey));
   };
   const updateCell = (keys: string[], rawValue: string | boolean) => {
      props.onChange?.(setPLACubeCellValue(props.value, keys, parseCubeInputValue(rawValue, props.definition)));
   };
   const renderCell = (keys: string[]) => {
      const value = getPLACubeCellValue(props.value, keys);
      const isReadOnly = props.readOnly || !props.onChange;
      if (props.definition.valueType === "boolean") return <input disabled={isReadOnly} type="checkbox" checked={Boolean(value)} onChange={(event) => updateCell(keys, event.target.checked)} />;
      return <input disabled={isReadOnly} type={inputType} value={cubeInputValue(value)} onChange={(event) => updateCell(keys, event.target.value)} />;
   };
   const getGridCellKeys = (rowKey: string, columnKey: string): string[] => selectedCellKeys.map((key, dimensionIndex) => {
      if (dimensionIndex === expandedDimensionIndexes[0]) return rowKey;
      if (dimensionIndex === expandedDimensionIndexes[1]) return columnKey;
      return key;
   });
   const sumGridCells = (rowsToSum: string[], columnsToSum: string[]) => rowsToSum.reduce((total, rowKey) => total + columnsToSum.reduce((columnTotal, columnKey) => {
      const numericValue = Number(getPLACubeCellValue(props.value, getGridCellKeys(rowKey, columnKey)));
      return Number.isFinite(numericValue) ? columnTotal + numericValue : columnTotal;
   }, 0), 0);
   const renderGridTotal = (rowsToSum: string[], columnsToSum: string[]) => formatPLAAttributeValue(sumGridCells(rowsToSum, columnsToSum), props.definition);

   return (
      <AdvancedDragModal title={props.definition.name || Localization.getText("Cube dimensions")} PosDefault={{ width: "820px", left: "50%", transform: "translateX(-50%)", top: "100px" }} onClose={props.onClose}>
         <ADModalBody>
            <div className="pla-cube-popup">
               {selectorDimensionIndexes.length ? <div className="pla-cube-popup__selectors">
                  {selectorDimensionIndexes.map((dimensionIndex) => {
                     const option = dimensionOptions[dimensionIndex];
                     return <label key={`${option.dimension.elementTypeId}-${dimensionIndex}`}>
                        <span>{option.name}</span>
                        <select value={selectedCellKeys[dimensionIndex]} onChange={(event) => updateSelectedKey(dimensionIndex, event.target.value)}>
                           {option.elements.map((element) => <option key={element.id} value={element.id}>{getPLACubeElementName(element)}</option>)}
                        </select>
                     </label>;
                  })}
               </div> : null}
               {rowDimension ? <div className="pla-cube-popup__grid-wrap">
                  <table className="pla-cube-grid">
                     <thead>
                        <tr>
                           <th>{rowDimension.name}</th>
                           {columnDimension ? columnKeys.map((columnKey) => (
                              <th key={columnKey}>{getPLACubeElementName(columnDimension.elements.find((element) => element.id === columnKey)!)}</th>
                           )) : <th>{Localization.getText("Value")}</th>}
                           {showNumericTotals ? <th>{Localization.getText("Cube total")}</th> : null}
                        </tr>
                     </thead>
                     <tbody>
                        {rowKeys.map((rowKey) => (
                           <tr key={rowKey}>
                              <td>{getPLACubeElementName(rowDimension.elements.find((element) => element.id === rowKey)!)}</td>
                              {columnKeys.map((columnKey) => {
                                 const keys = getGridCellKeys(rowKey, columnKey);
                                 return <td key={columnKey}>{renderCell(keys)}</td>;
                              })}
                              {showNumericTotals ? <td className="pla-cube-grid__total">{renderGridTotal([rowKey], columnKeys)}</td> : null}
                           </tr>
                        ))}
                        {showNumericTotals ? <tr className="pla-cube-grid__total-row">
                           <td>{Localization.getText("Cube total")}</td>
                           {columnKeys.map((columnKey) => (
                              <td key={columnKey} className="pla-cube-grid__total">{renderGridTotal(rowKeys, [columnKey])}</td>
                           ))}
                           <td className="pla-cube-grid__total">{renderGridTotal(rowKeys, columnKeys)}</td>
                        </tr> : null}
                     </tbody>
                  </table>
               </div> : <span>{Localization.getText("No cube dimensions configured")}</span>}
            </div>
         </ADModalBody>
         <ADModalFooter><GlassButtonRow><GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton></GlassButtonRow></ADModalFooter>
      </AdvancedDragModal>
   );
}

export function PLACubeAttributeView(props: {
   project: PLAProject;
   elements: PLAElementRecord[];
   definition: PLAAttributeDefinition;
   value: any;
   filter?: PLACubeFilter;
   readOnly?: boolean;
   onChange?: (value: any) => void;
}) {
   const [showGrid, setShowGrid] = useState(false);
   const totalValue = formatPLAAttributeValue(getPLACubeFilteredValue(props.value, props.definition, props.project, props.elements, props.filter), props.definition);
   return (
      <span className="pla-cube-summary">
         <span>{totalValue}</span>
         <GlassButton className="pla-cube-summary__expand" title={Localization.getText("Show cube values")} aria-label={Localization.getText("Show cube values")} onClick={(event) => { event.stopPropagation(); setShowGrid(true); }}>
            <MIBIcon icon="expandable" />
         </GlassButton>
         {showGrid ? createPortal(<CubeGridModal {...props} onClose={() => setShowGrid(false)} />, overlayRoot()) : null}
      </span>
   );
}